import { ReactNode, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useModalContext } from './ModalProvider';
import { userAPI } from '../UserAPI';
import dayjs from 'dayjs';
import Metric from './Metric';
import { useFormats } from '../hooks/formats';
import { pdf } from '@react-pdf/renderer';
import { downloadFile, downloadInvoice, PDFInvoice } from '../billing/invoice';
import { useAdministrationAPI } from '../AdministrationAPI';

import { Column, ColumnDef } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, FileCode, FileText, Info, Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

interface MarketplacePaymentSummaryProps {
    customerId: string;
}

interface PaymentSummary {
    totalRevenue: number;
    totalExpenses: number;
    netAmount: number;
    pendingPayments: number;
    recentTransactions: Array<{
        id: string;
        amount: number;
        description: string;
        date: string;
        status: 'completed' | 'pending' | 'failed';
        type: 'income' | 'expense';
        invoiceId: string;
    }>;
}

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({ column, children }: { column: Column<any, unknown>; children: ReactNode }) {
    const sorted = column.getIsSorted();

    return (
        <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 gap-1 px-3 font-bold text-foreground"
            onClick={() => column.toggleSorting(sorted === "asc")}
        >
            {children}
            {sorted === "asc" ? (
                <ArrowUp aria-hidden className="h-3.5 w-3.5" />
            ) : sorted === "desc" ? (
                <ArrowDown aria-hidden className="h-3.5 w-3.5" />
            ) : (
                <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
            )}
        </Button>
    );
}

export default function MarketplacePaymentSummary({ customerId }: MarketplacePaymentSummaryProps) {
    const { t } = useTranslation();
    const { pushError } = useModalContext() as any;
    const { currencyFormat } = useFormats();
    const { getInvoiceById } = useAdministrationAPI();
    const [summary, setSummary] = useState<PaymentSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [customer, setCustomer] = useState() as any;

    useEffect(() => {
        loadPaymentSummary();
        loadCustomer();
    }, [customerId]);

    const loadCustomer = async () => {
        try {
            const newCustomer = await userAPI.getCustomer(customerId);
            newCustomer.address = newCustomer.address || {};
            setCustomer(newCustomer);
        } catch (error) {
            console.error('Failed to load customer:', error);
        }
    };

    const loadPaymentSummary = async () => {
        try {
            setLoading(true);

            // Try to get real payment summary from API
            try {
                const paymentSummary = await userAPI.getPaymentSummary(customerId) as any;
                if (paymentSummary) {
                    setSummary(paymentSummary);
                    return;
                }
            } catch (apiError) {
                console.log('Payment summary API not available, using mock data');
            }
        } catch (error) {
            pushError(error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'text-success';
            case 'pending': return 'text-warning';
            case 'failed': return 'text-destructive';
            default: return 'text-muted-foreground';
        }
    };

    const getTypeIcon = (type: string) => {
        return <span className={type === 'income' ? 'text-success' : 'text-destructive'}>{type === 'income' ? '↑' : '↓'}</span>;
    };

    const transactionColumns: ColumnDef<any>[] = [
        {
            id: "date",
            accessorFn: (transaction: any) => transaction.date ? new Date(transaction.date).getTime() : 0,
            header: ({ column }) => (
                <SortableHeader column={column}>{t("billingPanel.dateColumn") || "Datum"}</SortableHeader>
            ),
            cell: ({ row }) => (
                <div className="text-sm">
                    {dayjs(row.original.date).format(t("global.dateShort") || "DD.MM.YYYY")}
                </div>
            ),
        },
        {
            id: "description",
            accessorFn: (transaction: any) => (transaction.description || "").toLowerCase(),
            header: ({ column }) => (
                <SortableHeader column={column}>{t("billingPanel.senderRecipientColumn") || "Beschreibung"}</SortableHeader>
            ),
            cell: ({ row }) => (
                <div className="flex items-center gap-2 text-sm">
                    {getTypeIcon(row.original.type)}
                    <span>{row.original.description}</span>
                </div>
            ),
        },
        {
            id: "amount",
            accessorFn: (transaction: any) => transaction.amount || 0,
            header: ({ column }) => (
                <div className="text-right">
                    <SortableHeader column={column}>{t("billingPanel.amountColumn") || "Betrag"}</SortableHeader>
                </div>
            ),
            cell: ({ row }) => (
                <div className="text-right text-sm font-bold">
                    {currencyFormat.format(row.original.amount)}
                </div>
            ),
        },
        {
            id: "status",
            accessorFn: (transaction: any) => {
                if (transaction.status === 'completed') return 1;
                if (transaction.status === 'pending') return 2;
                return 3;
            },
            header: ({ column }) => (
                <SortableHeader column={column}>{t("billingPanel.paymentStatusColumn") || "Status"}</SortableHeader>
            ),
            cell: ({ row }) => (
                <div className={`text-sm font-bold ${getStatusColor(row.original.status)}`}>
                    {row.original.status === 'completed' ? (t("billingPanel.paid") || "Abgeschlossen") :
                     row.original.status === 'pending' ? (t("billingPanel.open") || "Ausstehend") :
                     (t("billingPanel.failed") || "Fehlgeschlagen")}
                </div>
            ),
        },
        {
            id: "actions",
            header: () => "",
            cell: ({ row }) => {
                const transaction = row.original;

                if (!transaction.invoiceId) {
                    return null;
                }

                return (
                    <div className="flex">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    aria-label={t("billingPanel.downloadInvoiceForecastPDF")}
                                    onClick={async () => {
                                        try {
                                            const invoice = await getInvoiceById(customerId, transaction.invoiceId);
                                            const blob = await pdf(
                                                <PDFInvoice
                                                    invoice={invoice}
                                                    customer={customer}
                                                ></PDFInvoice>
                                            ).toBlob();
                                            downloadFile(blob, `${t("billingPanel.invoice")} ${invoice.id}`);
                                        } catch (error: any) {
                                            pushError(error);
                                        }
                                    }}
                                >
                                    <FileText className="h-4 w-4" aria-hidden />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("billingPanel.downloadInvoiceForecastPDF")}</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    aria-label={t("billingPanel.downloadInvoiceForecastERechnung")}
                                    onClick={async () => {
                                        try {
                                            const invoice = await getInvoiceById(customerId, transaction.invoiceId);
                                            downloadInvoice(invoice);
                                        } catch (error: any) {
                                            pushError(error);
                                        }
                                    }}
                                >
                                    <FileCode className="h-4 w-4" aria-hidden />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("billingPanel.downloadInvoiceForecastERechnung")}</TooltipContent>
                        </Tooltip>
                    </div>
                );
            },
        },
    ];

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                Lade Zahlungsübersicht...
            </div>
        );
    }

    if (!summary) {
        return (
            <Alert variant="info">
                <Info aria-hidden className="h-4 w-4" />
                <AlertDescription>Keine Zahlungsdaten verfügbar.</AlertDescription>
            </Alert>
        );
    }

    return (
        <TooltipProvider>
            <div>
                <h3 className="mb-5 text-lg font-semibold">Marktplatz Zahlungsübersicht</h3>

                {/* Summary Cards */}
                <div className="mb-6 flex flex-wrap gap-4">
                    <Metric
                        title="Gesamteinnahmen"
                        value={formatCurrency(summary.totalRevenue)}
                        color="var(--colorGreen)"
                        className="grow rounded-md border p-4"
                    />
                    <Metric
                        title="Gesamtausgaben"
                        value={formatCurrency(summary.totalExpenses)}
                        color="var(--colorRed)"
                        className="grow rounded-md border p-4"
                    />
                    <Metric
                        title="Netto-Betrag"
                        value={formatCurrency(summary.netAmount)}
                        color="var(--colorNeutralPrimary)"
                        className="grow rounded-md border p-4"
                    />
                    <Metric
                        title="Ausstehende Zahlungen"
                        value={formatCurrency(summary.pendingPayments)}
                        color="var(--colorOrange)"
                        className="grow rounded-md border p-4"
                    />
                </div>

                {/* Recent Transactions */}
                <div className="mt-5">
                    <h4 className="mb-5 text-sm font-semibold">Letzte Transaktionen</h4>
                    <div className="overflow-x-auto">
                        <DataTable
                            columns={transactionColumns}
                            data={summary.recentTransactions || []}
                            emptyMessage={t("settingsPanel.projectsPanel.noObjectsDefined") || "Keine Transaktionen verfügbar"}
                        />
                    </div>
                </div>
            </div>
        </TooltipProvider>
    );
}
